import { useState, useCallback, useMemo, useEffect } from 'react';
import type { CheckedState } from '@radix-ui/react-checkbox';
import type { Source } from '@/types/source-types';
import {
  computeVisibleSelectionInfo,
  deriveHeaderCheckboxState,
  toggleFileSelection as toggleFileSelectionHelper,
  toggleSelectAllVisible as toggleSelectAllVisibleHelper,
} from '../collection-selection-helpers';

interface UseFileSelectionOptions {
  sources: Source[];
  filteredSources: Source[];
}

interface UseFileSelectionReturn {
  selectedFiles: Set<string>;
  selectedFileNames: string[];
  selectedFilesCount: number;
  hasSelection: boolean;
  headerCheckboxState: CheckedState;
  toggleFileSelection: (filename: string, selected: boolean) => void;
  toggleSelectAllVisible: (selected: boolean) => void;
  clearSelection: () => void;
  setSelectedFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export function useFileSelection({
  sources,
  filteredSources,
}: UseFileSelectionOptions): UseFileSelectionReturn {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  // Sync selection with sources - remove invalid selections
  useEffect(() => {
    setSelectedFiles((prev) => {
      if (prev.size === 0) return prev;

      const validNames = new Set(sources.map((source) => source.filename));
      let changed = false;
      const next = new Set<string>();

      prev.forEach((name) => {
        if (validNames.has(name)) {
          next.add(name);
        } else {
          changed = true;
        }
      });

      if (!changed && next.size === prev.size) return prev;
      return next;
    });
  }, [sources]);

  const selectedFileNames = useMemo(
    () => Array.from(selectedFiles),
    [selectedFiles],
  );

  const selectedFilesCount = selectedFileNames.length;
  const hasSelection = selectedFilesCount > 0;

  const toggleFileSelection = useCallback(
    (filename: string, selected: boolean) => {
      setSelectedFiles((prev) =>
        toggleFileSelectionHelper(prev, filename, selected),
      );
    },
    [],
  );

  const toggleSelectAllVisible = useCallback(
    (selected: boolean) => {
      setSelectedFiles((prev) =>
        toggleSelectAllVisibleHelper(prev, filteredSources, selected),
      );
    },
    [filteredSources],
  );

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  const visibleSelectionInfo = useMemo(
    () => computeVisibleSelectionInfo(filteredSources, selectedFiles),
    [filteredSources, selectedFiles],
  );

  const headerCheckboxState: CheckedState =
    deriveHeaderCheckboxState(visibleSelectionInfo);

  return {
    selectedFiles,
    selectedFileNames,
    selectedFilesCount,
    hasSelection,
    headerCheckboxState,
    toggleFileSelection,
    toggleSelectAllVisible,
    clearSelection,
    setSelectedFiles,
  };
}
